import { useState } from "react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type DailyAIUpdatesHeroProps = {
  onEmailContinue?: (email: string) => void;
};

export default function DailyAIUpdatesHero({
  onEmailContinue,
}: DailyAIUpdatesHeroProps) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  function handleJoin() {
    const v = email.trim();
    if (!EMAIL_RE.test(v)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setEmailError("");
    onEmailContinue?.(v);
  }

  return (
    <section
      id="daily-ai-updates-hero"
      className="w-full bg-black py-10 font-fira text-white"
    >
      <div className="mx-auto w-[90%] md:w-[98%]">
        <div className="rounded-b-2xl rounded-t-none p-px md:rounded-b-3xl">
          <div className="relative min-h-[min(80dvh,44rem)] overflow-hidden rounded-3xl border border-[#FFFFFF29] md:min-h-[min(65dvh,40rem)] md:rounded-b-3xl">
            <picture className="pointer-events-none absolute inset-0">
              <source
                media="(min-width: 768px)"
                srcSet="/business/dailyaid.webp"
              />
              <img
                src="/business/dailym.webp"
                alt=""
                className="h-full w-full object-cover object-center"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </picture>

            <div className="pointer-events-none absolute inset-0" />

            <div className="relative z-10 flex min-h-[min(80dvh,44rem)] flex-col justify-end px-5 py-12 md:min-h-[min(65dvh,40rem)] md:justify-center md:px-16 md:py-16 lg:px-24">
              <div className="flex flex-col items-start md:max-w-xl">
                <h1 className="font-fira-condensed text-[36px] font-medium leading-tight tracking-tight text-white md:text-[56px] md:font-semibold">
                 The full library of everything we've shared.
                </h1>

                <p className="font-fira mt-3 text-[14px] font-normal leading-relaxed text-[#FFFFFF80] md:mt-4 md:text-[18px]">
                  Every brief, prompt, slide, and resource shared in the Staying Ahead community. Archived so you can catch up anytime
                </p>

                <div className="mt-6 w-full max-w-lg md:mt-8">
                  <div
                    className="flex flex-col gap-2 sm:gap-3 md:flex-row md:items-stretch"
                    role="group"
                    aria-label="Email signup"
                  >
                    <label className="sr-only" htmlFor="daily-ai-hero-email">
                      Email address
                    </label>
                    <input
                      id="daily-ai-hero-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="Enter your email id"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleJoin();
                        }
                      }}
                      aria-invalid={!!emailError}
                      aria-describedby={
                        emailError ? "daily-ai-hero-email-error" : undefined
                      }
                      className={`min-h-12 min-w-0 flex-1 rounded-lg border bg-black/40 px-3 py-3 text-center font-fira text-sm text-white placeholder:text-white/35 backdrop-blur-sm focus:outline-none focus:ring-1 focus:ring-white/30 sm:px-4 sm:text-[0.9375rem] md:text-left ${
                        emailError
                          ? "border-red-400/50 focus:border-red-400/50"
                          : "border-white/20 focus:border-white/40"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={handleJoin}
                      className="inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-lg border border-[#FFFFFF4D] bg-white px-5 py-3 font-fira text-sm font-semibold text-black transition-opacity hover:opacity-90 sm:px-6 sm:text-[0.9375rem]"
                    >
                      <img
                        src="/resources/wp.svg"
                        alt=""
                        width={17}
                        height={16}
                        aria-hidden="true"
                      />
                      Join WhatsApp Community
                    </button>
                  </div>
                  {emailError ? (
                    <p
                      id="daily-ai-hero-email-error"
                      className="mt-2 text-xs text-red-400"
                      role="alert"
                    >
                      {emailError}
                    </p>
                  ) : null}
                  <p className="mt-3 text-xs text-white/45">
                    1M+ members · Free · Unsubscribe Anytime
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
